import type { PageCtx } from "./context";
import { hoursKey, isGenerated, phoneKey } from "./facts";
import { langCode } from "./theme";
import { arr, str, strs, type Rec } from "./ui";

// Local SEO: per-page title/description/canonical/OG, NGO + FAQPage + Service + BreadcrumbList,
// static favicons from /public, correct html lang. Written into <head> at runtime.

type PageLike = {
  kind?: string;
  name?: string;
  title?: string;
  description?: string;
  keywords?: string;
  sections?: Record<string, Rec>;
  images?: Record<string, { src?: string; alt?: string }>;
};
type SiteLike = { name?: string; lang?: string; theme?: { accent?: string }; pages?: PageLike[]; formUrl?: string };

function upsert(selector: string, create: () => HTMLElement): HTMLElement {
  let el = document.head.querySelector<HTMLElement>(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
}

const meta = (attr: "name" | "property", key: string, content: string) => {
  if (!content) return;
  const el = upsert(`meta[${attr}="${key}"]`, () => {
    const m = document.createElement("meta");
    m.setAttribute(attr, key);
    return m;
  }) as HTMLMetaElement;
  el.content = content;
};

const link = (rel: string, href: string, extra?: Record<string, string>) => {
  const sel = extra?.sizes ? `link[rel="${rel}"][sizes="${extra.sizes}"]` : `link[rel="${rel}"]${extra?.type ? `[type="${extra.type}"]` : ""}`;
  const el = upsert(sel, () => {
    const l = document.createElement("link");
    l.rel = rel;
    return l;
  }) as HTMLLinkElement;
  el.href = href;
  Object.entries(extra || {}).forEach(([k, v]) => el.setAttribute(k, v));
};

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const dayIndex = (s: string) => DAYS.findIndex((d) => s.trim().toLowerCase().startsWith(d.slice(0, 3).toLowerCase()));

function expandDays(label: string): string[] {
  const out: string[] = [];
  for (const part of label.split(/\s*(?:,|&|\band\b|\+)\s*/i)) {
    const range = part.split(/\s*(?:[-–—]|\bto\b|\bthrough\b|\bthru\b)\s*/i);
    const a = dayIndex(range[0] || "");
    if (a < 0) continue;
    if (range.length < 2) {
      out.push(DAYS[a]);
      continue;
    }
    const b = dayIndex(range[range.length - 1]);
    if (b < 0) continue;
    for (let i = a; ; i = (i + 1) % 7) {
      out.push(DAYS[i]);
      if (i === b) break;
    }
  }
  return out;
}

function to24h(s: string): string | null {
  const m = s.trim().match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/i);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = m[2] || "00";
  const ap = (m[3] || "").toLowerCase();
  if (ap === "pm" && h < 12) h += 12;
  if (ap === "am" && h === 12) h = 0;
  return h > 24 ? null : `${String(h).padStart(2, "0")}:${min}`;
}

function openingHours(hours: { day: string; time: string }[]) {
  const specs: Rec[] = [];
  for (const row of hours) {
    if (/closed/i.test(row.time)) continue;
    const days = expandDays(row.day);
    const t = row.time.split(/\s*(?:[-–—]|\bto\b)\s*/i);
    const opens = t[0] ? to24h(t[0]) : null;
    const closes = t[1] ? to24h(t[1]) : null;
    if (!days.length || !opens || !closes) continue;
    specs.push({ "@type": "OpeningHoursSpecification", dayOfWeek: days, opens, closes });
  }
  return specs;
}

const httpsOnly = (u?: string) => (u && /^https:\/\//.test(u) ? u : undefined);

/** Parse "10674 Vista Lane, Moreno Valley, CA 92557, United States" into PostalAddress fields. */
function postalAddress(raw: string): Rec {
  const parts = raw.split(",").map((p) => p.trim()).filter(Boolean);
  const addr: Rec = { "@type": "PostalAddress" };
  if (parts.length >= 4) {
    addr.streetAddress = parts[0];
    addr.addressLocality = parts[1];
    const regionZip = parts[2].match(/^([A-Z]{2})\s+(\d{5}(?:-\d{4})?)$/i);
    if (regionZip) {
      addr.addressRegion = regionZip[1].toUpperCase();
      addr.postalCode = regionZip[2];
    } else {
      addr.addressRegion = parts[2];
    }
    addr.addressCountry = /united states|usa|us/i.test(parts[3]) ? "US" : parts[3];
  } else if (/\d/.test(raw)) {
    addr.streetAddress = raw;
  } else {
    addr.addressLocality = raw;
  }
  return addr;
}

function applyIcons() {
  link("icon", "/favicon.ico", { sizes: "any" });
  link("icon", "/favicon.svg", { type: "image/svg+xml" });
  link("icon", "/favicon-32x32.png", { type: "image/png", sizes: "32x32" });
  link("apple-touch-icon", "/apple-touch-icon.png", { sizes: "180x180" });
  link("manifest", "/site.webmanifest");
}

export function applySeo(site: SiteLike, page: PageLike, ctx: PageCtx) {
  const origin = location.origin;
  const url = origin + location.pathname;
  const home = site.pages?.find((p) => p.kind === "home");
  const title = str(page.title) || ctx.brand;
  const desc = str(page.description);
  const keywords = str(page.keywords);
  const heroImg = httpsOnly((home?.images || {}).hero?.src) || httpsOnly(page.images?.hero?.src);
  const email = str(home?.sections?.footer?.email) || str(page.sections?.footer?.email) || str(page.sections?.orgLocation?.email);

  document.documentElement.lang = langCode(ctx.lang);
  document.title = title;
  meta("name", "description", desc);
  if (keywords) meta("name", "keywords", keywords);
  meta("name", "robots", "index, follow");
  meta("name", "author", ctx.brand);
  meta("name", "theme-color", "#2d6a4f");
  link("canonical", url);
  applyIcons();

  meta("property", "og:type", "website");
  meta("property", "og:site_name", ctx.brand);
  meta("property", "og:locale", "en_US");
  meta("property", "og:title", title);
  meta("property", "og:description", desc);
  meta("property", "og:url", url);
  if (heroImg) meta("property", "og:image", heroImg);
  meta("name", "twitter:card", heroImg ? "summary_large_image" : "summary");
  meta("name", "twitter:title", title);
  meta("name", "twitter:description", desc);
  if (heroImg) meta("name", "twitter:image", heroImg);

  const bizId = `${origin}/#business`;
  const areas = strs(home?.sections?.footer?.areas);
  const business: Rec = {
    "@type": ["NGO", "LocalBusiness"],
    "@id": bizId,
    name: ctx.brand,
    url: `${origin}/`,
    logo: `${origin}/android-chrome-512x512.png`,
  };
  const homeDesc = str(home?.description);
  if (homeDesc) business.description = homeDesc;
  if (heroImg) business.image = heroImg;
  if (email) business.email = email;
  if (ctx.phone && !isGenerated(ctx.generated?.phone, phoneKey(ctx.phone))) business.telephone = ctx.phone;
  if (ctx.address) business.address = postalAddress(ctx.address);
  const specs = isGenerated(ctx.generated?.hours, hoursKey(ctx.hours)) ? [] : openingHours(ctx.hours);
  if (specs.length) business.openingHoursSpecification = specs;
  if (areas.length) business.areaServed = areas.map((name) => ({ "@type": "Place", name }));

  const graph: Rec[] = [
    business,
    {
      "@type": "WebSite",
      "@id": `${origin}/#website`,
      name: ctx.brand,
      url: `${origin}/`,
      inLanguage: langCode(ctx.lang),
      publisher: { "@id": bizId },
    },
  ];

  const faq = arr(page.sections?.faq?.items).filter((q) => str(q.question) && str(q.answer));
  if (faq.length) {
    graph.push({
      "@type": "FAQPage",
      mainEntity: faq.map((q) => ({
        "@type": "Question",
        name: str(q.question),
        acceptedAnswer: { "@type": "Answer", text: str(q.answer) },
      })),
    });
  }
  if (page.kind === "service" && page.name) {
    graph.push({
      "@type": "Service",
      name: page.name,
      description: str(page.sections?.hero?.subtitle) || desc,
      provider: { "@id": bizId },
      ...(areas.length ? { areaServed: areas } : {}),
    });
  }
  if (page.kind && page.kind !== "home") {
    graph.push({
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: str(ctx.ui.home) || "Home", item: `${origin}/` },
        { "@type": "ListItem", position: 2, name: str(page.name) || title, item: url },
      ],
    });
  }

  const script = upsert("script#flash-jsonld", () => {
    const s = document.createElement("script");
    s.id = "flash-jsonld";
    s.type = "application/ld+json";
    return s;
  });
  script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
}
